// ============================================================================
// CombatEngine.js - Core Tick-Based Combat Simulation Engine
// ============================================================================
// ROV Realistic Combat Simulator
// Tick-based engine that processes combat actions every 100ms
// ============================================================================

// Import Spell Logic
import { CHALLENGER_SPELLS } from './SpellPassives';
import { inferSkillEffectsFromDescription } from './ExcelTextParsers';

export const TICK_MS = 100; // 100ms per tick
export const TICKS_PER_SECOND = 1000 / TICK_MS; // 10 ticks/s

// ============================================================================
// ENUMS
// ============================================================================

export const DamageType = {
  PHYSICAL: 'Physical',
  MAGIC: 'Magic',
  TRUE: 'True',
  MIXED: 'Physical/Magic',
};

export const ActionType = {
  AUTO_ATTACK: 'AUTO_ATTACK',
  SKILL: 'SKILL',
  ITEM_ACTIVE: 'ITEM_ACTIVE',
  SPELL: 'SPELL',
  IDLE: 'IDLE',
};

export const CCType = {
  STUN: 'Stun',
  SLOW: 'Slow',
  SILENCE: 'Silence',
  KNOCKUP: 'Knockup',
  KNOCKBACK: 'Knockback',
  SUPPRESS: 'Suppress',
  FREEZE: 'Freeze',
  PETRIFY: 'Petrify',
  PULL: 'Pull',
  TAUNT: 'Taunt',
  IMMOBILIZE: 'Immobilize',
};

// CC types that prevent ALL actions (hard CC)
export const HARD_CC = [CCType.STUN, CCType.KNOCKUP, CCType.KNOCKBACK, CCType.SUPPRESS, CCType.FREEZE, CCType.PETRIFY];
// CC types that prevent skill use only
export const SILENCE_CC = [CCType.SILENCE];

export const BuffType = {
  STAT_FLAT: 'STAT_FLAT',
  STAT_PCT: 'STAT_PCT',
  DAMAGE_AMP: 'DAMAGE_AMP',
  DAMAGE_REDUCTION: 'DAMAGE_REDUCTION',
  SHIELD: 'SHIELD',
  INVULNERABLE: 'INVULNERABLE',
  UNTARGETABLE: 'UNTARGETABLE',
  NEXT_AA_BONUS: 'NEXT_AA_BONUS',
  ON_HIT_EFFECT: 'ON_HIT_EFFECT',
  DOT: 'DOT',
  HOT: 'HOT',
  CC: 'CC',
};

// ============================================================================
// HERO STATE
// ============================================================================

export function createHeroState(hero, level, items = [], arcana = [], skillRanks = {}) {
  const baseStats = getBaseStatsAtLevel(hero, level);
  const itemStats = computeItemStats(items);
  const arcanaStats = computeArcanaStats(arcana);

  // Merge all stats
  const totalStats = mergeStats(baseStats, itemStats, arcanaStats);

  return {
    heroName: hero.hero_name || hero.name,
    heroData: hero,
    level,
    items,
    arcana,
    skillRanks, // { 'Skill 1': 3, 'Skill 2': 2, 'Ultimate': 1 }

    // Current state (mutable during combat)
    currentHP: totalStats.hp,
    maxHP: totalStats.hp,
    currentMana: totalStats.max_mana,
    maxMana: totalStats.max_mana,

    // Computed stats (recalculated when buffs change)
    baseStats,
    itemStats,
    arcanaStats,
    totalStats,

    // Combat state
    cooldowns: {}, // { 'Skill 1': ticksRemaining }
    buffs: [],     // [{ type, value, duration, source, ... }]
    debuffs: [],   // [{ type, value, duration, source, ... }]
    shields: [],   // [{ amount, duration, source, ... }]

    // Item passive state
    itemPassiveState: {},  // per-item internal state (cooldowns, stacks, etc.)

    // Hero passive state
    heroPassiveState: {
      stacks: 0,
      internalCD: 0,
      customData: {},
    },

    // Action queue
    actionQueue: [],
    currentAction: null,
    actionProgress: 0, // ticks into current action

    // Auto-attack tracking
    aaCount: 0,     // total AA count (for passives like "every 3rd hit")
    lastSkillTick: -999, // last tick a skill was used (for after-skill triggers)

    // Tracking stats
    totalDamageDealt: 0,
    totalDamageTaken: 0,
    totalHealing: 0,
    totalShielded: 0,
    skillsUsed: 0,
    aaCasts: 0,
    critCount: 0,
    ccApplied: 0,

    // State flags
    isAlive: true,
    isDead: false,
    isUntargetable: false,
    isInvulnerable: false,
    isStunned: false,
    isSilenced: false,
  };
}

// ============================================================================
// STATS COMPUTATION
// ============================================================================

const STAT_ALIAS_MAP = {
  phys_def: 'phys_defense',
  magic_def: 'magic_defense',
  move_speed: 'movement_speed',
  move_speed_pct: 'movement_speed_pct',
  crit_rate: 'critical_rate',
  crit_dmg: 'critical_damage',
};

const PERCENT_STATS = new Set([
  'attack_speed',
  'critical_rate',
  'life_steal',
  'magic_life_steal',
  'cooldown_reduction',
  'armor_pierce_pct',
  'magic_pierce_pct',
  'movement_speed_pct',
]);

function canonicalStatKey(key) {
  if (!key) return key;
  return STAT_ALIAS_MAP[key] || key;
}

function normalizeStatScales(stats) {
  const out = { ...stats };

  for (const key of PERCENT_STATS) {
    const val = out[key];
    if (typeof val === 'number' && Number.isFinite(val) && val > 1) {
      out[key] = val / 100;
    }
  }

  if (typeof out.critical_damage === 'number' && Number.isFinite(out.critical_damage) && out.critical_damage > 3) {
    out.critical_damage = out.critical_damage / 100;
  }

  return out;
}

export function getBaseStatsAtLevel(hero, level) {
  // hero.levels is a map: { 1: { hp, phys_atk, ... }, 2: { ... }, ... }
  if (hero.levels && hero.levels[level]) {
    const raw = hero.levels[level] || {};
    const normalized = createEmptyStats();
    for (const [k, v] of Object.entries(raw)) {
      const key = canonicalStatKey(k);
      if (typeof v === 'number' && normalized.hasOwnProperty(key)) {
        normalized[key] = v;
      }
    }
    return normalizeStatScales(normalized);
  }
  // Fallback: use level 1 stats
  return normalizeStatScales({
    hp: hero.hp || 3000,
    phys_atk: hero.phys_atk || 170,
    magic_power: hero.magic_power || 0,
    phys_defense: hero.phys_defense || hero.phys_def || 100,
    magic_defense: hero.magic_defense || hero.magic_def || 50,
    max_mana: hero.max_mana || hero.mana || 400,
    movement_speed: hero.movement_speed || hero.move_speed || 380,
    armor_pierce: hero.armor_pierce || 0,
    armor_pierce_pct: hero.armor_pierce_pct || 0,
    magic_pierce: hero.magic_pierce || 0,
    magic_pierce_pct: hero.magic_pierce_pct || 0,
    attack_speed: hero.attack_speed || 0,
    critical_rate: hero.critical_rate || hero.crit_rate || 0,
    critical_damage: hero.critical_damage || hero.crit_dmg || 2, // 200% base
    life_steal: hero.life_steal || 0,
    magic_life_steal: hero.magic_life_steal || 0,
    cooldown_reduction: hero.cooldown_reduction || 0,
  });
}

export function computeItemStats(items) {
  const stats = createEmptyStats();
  for (const item of items) {
    if (!item) continue;

    // Support both item.stats (nested) and item[key] (flat)
    const source = item.stats || item;

    for (const [rawKey, value] of Object.entries(source)) {
      const key = canonicalStatKey(rawKey);
      // Ensure key is a valid stat key and value is a number
      if (typeof value === 'number' && stats.hasOwnProperty(key)) {
        stats[key] += value;
      }
    }
  }
  return normalizeStatScales(stats);
}

export function computeArcanaStats(arcana) {
  const stats = createEmptyStats();
  for (const a of arcana) {
    if (!a || !a.stats) continue;
    for (const [rawKey, value] of Object.entries(a.stats)) {
      const key = canonicalStatKey(rawKey);
      if (typeof value === 'number' && stats.hasOwnProperty(key)) {
        stats[key] += value;
      }
    }
  }
  return normalizeStatScales(stats);
}

export function createEmptyStats() {
  return {
    hp: 0,
    phys_atk: 0,
    magic_power: 0,
    phys_defense: 0,
    magic_defense: 0,
    max_mana: 0,
    movement_speed: 0,
    armor_pierce: 0,
    armor_pierce_pct: 0,
    magic_pierce: 0,
    magic_pierce_pct: 0,
    attack_speed: 0,
    critical_rate: 0,
    critical_damage: 0,
    life_steal: 0,
    magic_life_steal: 0,
    cooldown_reduction: 0,
    hp_regen: 0,
    mana_regen: 0,
  };
}

export function mergeStats(base, items, arcana) {
  const result = normalizeStatScales({ ...base });
  const additive = [items, arcana];

  for (const source of additive) {
    for (const [key, value] of Object.entries(source)) {
      if (typeof value === 'number') {
        result[key] = (result[key] || 0) + value;
      }
    }
  }

  // Clamp values
  result.critical_rate = Math.min(result.critical_rate, 1); // max 100%
  result.cooldown_reduction = Math.min(result.cooldown_reduction, 0.4); // max 40%
  result.armor_pierce_pct = Math.min(result.armor_pierce_pct, 1);
  result.magic_pierce_pct = Math.min(result.magic_pierce_pct, 1);

  return normalizeStatScales(result);
}

// Recalculate effective stats considering buffs/debuffs
export function getEffectiveStats(heroState) {
  const base = { ...heroState.totalStats };

  // Apply flat stat buffs
  for (const buff of heroState.buffs) {
    if (buff.type === BuffType.STAT_FLAT && buff.stat) {
      base[buff.stat] = (base[buff.stat] || 0) + buff.value;
    }
  }

  // Apply % stat buffs
  for (const buff of heroState.buffs) {
    if (buff.type === BuffType.STAT_PCT && buff.stat) {
      base[buff.stat] = (base[buff.stat] || 0) * (1 + buff.value);
    }
  }

  // Apply flat stat debuffs
  for (const debuff of heroState.debuffs) {
    if (debuff.type === BuffType.STAT_FLAT && debuff.stat) {
      base[debuff.stat] = (base[debuff.stat] || 0) - debuff.value;
    }
  }

  // Apply % stat debuffs
  for (const debuff of heroState.debuffs) {
    if (debuff.type === BuffType.STAT_PCT && debuff.stat) {
      base[debuff.stat] = (base[debuff.stat] || 0) * (1 - debuff.value);
    }
  }

  // Ensure non-negative
  for (const key of Object.keys(base)) {
    if (typeof base[key] === 'number' && base[key] < 0) {
      base[key] = 0;
    }
  }

  // Re-clamp
  base.critical_rate = Math.min(base.critical_rate, 1);
  base.cooldown_reduction = Math.min(base.cooldown_reduction, 0.4);

  return base;
}

// ============================================================================
// DAMAGE PIPELINE
// ============================================================================

/**
 * Full 7-stage damage pipeline
 * Returns detailed breakdown object
 */
export function calculateDamage(attacker, defender, damageSource) {
  const attackerStats = getEffectiveStats(attacker);
  const defenderStats = getEffectiveStats(defender);

  const breakdown = {
    stages: [],
    finalDamage: 0,
    damageType: damageSource.damageType || DamageType.PHYSICAL,
    isCrit: false,
    isExecute: false,
    source: damageSource.name || 'Unknown',
  };

  // ─── Stage 1: Base Damage ───
  let damage = computeBaseDamage(attackerStats, damageSource);
  breakdown.stages.push({ name: 'Base Damage', value: Math.round(damage) });

  // ─── Stage 2: Pre-Mitigation Modifiers ───
  // Critical Hit
  if (damageSource.canCrit !== false && damageSource.damageType !== DamageType.TRUE) {
    const critRoll = Math.random();
    if (critRoll < attackerStats.critical_rate) {
      const critMultiplier = attackerStats.critical_damage || 2;
      damage *= critMultiplier;
      breakdown.isCrit = true;
      breakdown.stages.push({ name: 'Critical Hit', value: Math.round(damage), multiplier: critMultiplier });
    }
  }

  // Damage Amplification (Fenrir, etc.)
  let damageAmp = 1;
  for (const buff of attacker.buffs) {
    if (buff.type === BuffType.DAMAGE_AMP) {
      damageAmp += buff.value;
    }
  }
  if (attackerStats._damageAmp) {
    damageAmp += attackerStats._damageAmp;
  }
  // Item passive: Fenrir's Tooth (+30% if target HP < 50%)
  if (defender.currentHP / defender.maxHP < 0.5) {
    const hasFenrir = attacker.items.some(i => i && i.name === "Fenrir's Tooth");
    if (hasFenrir) {
      damageAmp += 0.3;
      breakdown.stages.push({ name: "Fenrir's Tooth (+30%)", value: 'Active' });
    }
  }

  if (damageAmp !== 1) {
    damage *= damageAmp;
    breakdown.stages.push({ name: 'Damage Amplification', value: Math.round(damage), multiplier: damageAmp });
  }

  // ─── Stage 3: Penetration ───
  let effectiveDef = 0;
  if (damageSource.damageType === DamageType.PHYSICAL || damageSource.damageType === DamageType.MIXED) {
    effectiveDef = applyPenetration(
      defenderStats.phys_defense,
      attackerStats.armor_pierce,
      attackerStats.armor_pierce_pct
    );
    breakdown.stages.push({ name: 'Effective Phys Defense', value: Math.round(effectiveDef), original: Math.round(defenderStats.phys_defense) });
  } else if (damageSource.damageType === DamageType.MAGIC) {
    effectiveDef = applyPenetration(
      defenderStats.magic_defense,
      attackerStats.magic_pierce,
      attackerStats.magic_pierce_pct
    );
    breakdown.stages.push({ name: 'Effective Magic Defense', value: Math.round(effectiveDef), original: Math.round(defenderStats.magic_defense) });
  }
  // True damage skips defense

  // ─── Stage 4: Defense Reduction ───
  if (damageSource.damageType !== DamageType.TRUE) {
    const defenseMultiplier = 600 / (600 + Math.max(0, effectiveDef));
    const preDef = damage;
    damage *= defenseMultiplier;
    breakdown.stages.push({
      name: 'Defense Reduction',
      value: Math.round(damage),
      reduction: `${Math.round((1 - defenseMultiplier) * 100)}%`,
      formula: `600/(600+${Math.round(effectiveDef)}) = ${(defenseMultiplier * 100).toFixed(1)}%`
    });
  } else {
    breakdown.stages.push({ name: 'True Damage (bypasses defense)', value: Math.round(damage) });
  }

  // ─── Stage 5: Post-Mitigation ───
  // Damage Reduction buffs on defender
  let damageReduction = 0;
  for (const buff of defender.buffs) {
    if (buff.type === BuffType.DAMAGE_REDUCTION) {
      damageReduction += buff.value;
    }
  }
  if (defenderStats._damageReduction) {
    damageReduction += defenderStats._damageReduction;
  }
  damageReduction = Math.min(damageReduction, 0.9); // cap at 90%

  if (damageReduction > 0) {
    damage *= (1 - damageReduction);
    breakdown.stages.push({ name: 'Damage Reduction', value: Math.round(damage), reduction: `${Math.round(damageReduction * 100)}%` });
  }

  // Invulnerable check
  if (defender.isInvulnerable) {
    damage = 0;
    breakdown.stages.push({ name: 'Invulnerable', value: 0 });
  }

  // ─── Stage 6: Shield Absorption ───
  let shieldAbsorbed = 0;
  if (damage > 0 && defender.shields.length > 0) {
    const result = absorbShield(defender, damage);
    shieldAbsorbed = result.absorbed;
    damage = result.remaining;
    if (shieldAbsorbed > 0) {
      breakdown.stages.push({ name: 'Shield Absorbed', value: shieldAbsorbed, remaining: Math.round(damage) });
    }
  }

  // ─── Stage 7: Final Damage ───
  damage = Math.max(0, Math.round(damage));
  breakdown.finalDamage = damage;
  breakdown.stages.push({ name: 'Final Damage', value: damage });

  return breakdown;
}

// Compute base damage from skill data + scaling
function computeBaseDamage(attackerStats, source) {
  let damage = source.baseDamage || 0;

  // AD Scaling
  if (source.adScaling) {
    damage += attackerStats.phys_atk * source.adScaling;
  }

  // AP Scaling
  if (source.apScaling) {
    damage += attackerStats.magic_power * source.apScaling;
  }

  // HP Scaling (% max HP, current HP, lost HP)
  if (source.hpMaxScaling) {
    damage += attackerStats.hp * source.hpMaxScaling;
  }
  if (source.hpCurrentScaling) {
    // Uses target's current HP - will be passed in source
    damage += (source.targetCurrentHP || 0) * source.hpCurrentScaling;
  }
  if (source.targetMaxScaling) {
    damage += (source.targetMaxHP || 0) * source.targetMaxScaling;
  }
  if (source.hpLostScaling) {
    damage += (source.targetLostHP || 0) * source.hpLostScaling;
  }

  return damage;
}

// Apply penetration: flat first, then %
function applyPenetration(defense, flatPen, pctPen) {
  let effective = defense;
  // Step 1: Flat penetration
  effective = effective - (flatPen || 0);
  // Step 2: % penetration
  effective = effective * (1 - (pctPen || 0));
  return Math.max(0, effective);
}

// Absorb damage through shields
function absorbShield(heroState, damage) {
  let remaining = damage;
  let absorbed = 0;

  // Process shields in order (oldest first)
  const activeShields = heroState.shields.filter(s => s.amount > 0);
  for (const shield of activeShields) {
    if (remaining <= 0) break;
    const absorb = Math.min(shield.amount, remaining);
    shield.amount -= absorb;
    remaining -= absorb;
    absorbed += absorb;
  }

  // Remove depleted shields
  heroState.shields = heroState.shields.filter(s => s.amount > 0);

  return { remaining, absorbed };
}

// ============================================================================
// COMBAT LOG
// ============================================================================

export function createCombatLog() {
  return {
    events: [],
    addEvent(tick, type, data) {
      this.events.push({
        tick,
        time: (tick * TICK_MS / 1000).toFixed(1),
        type,
        ...data,
      });
    },
    getEvents() {
      return this.events;
    },
    getEventsByType(type) {
      return this.events.filter(e => e.type === type);
    },
    clear() {
      this.events = [];
    },
  };
}

// ============================================================================
// GAME STATE
// ============================================================================

export function createGameState(hero1State, hero2State) {
  return {
    tick: 0,
    timeSeconds: 0,
    hero1: hero1State,
    hero2: hero2State,
    combatLog: createCombatLog(),
    isFinished: false,
    winner: null,
    isPaused: false,
    speed: 1, // 1x, 2x, 4x
  };
}

// ============================================================================
// CORE ENGINE - TICK PROCESSOR
// ============================================================================

export function processTick(gameState) {
  if (gameState.isFinished || gameState.isPaused) return gameState;

  const { hero1, hero2, combatLog } = gameState;
  const tick = gameState.tick;

  // ─── Phase 1: Update Durations ───
  updateBuffDurations(hero1, tick, combatLog);
  updateBuffDurations(hero2, tick, combatLog);
  updateShieldDurations(hero1, tick);
  updateShieldDurations(hero2, tick);
  updateCooldowns(hero1);
  updateCooldowns(hero2);
  updateStateFlags(hero1);
  updateStateFlags(hero2);

  // ─── Phase 2: Process DoT/HoT ───
  processDoTs(hero1, hero2, tick, combatLog);
  processDoTs(hero2, hero1, tick, combatLog);
  processHoTs(hero1, tick, combatLog);
  processHoTs(hero2, tick, combatLog);

  // ─── Phase 3: Process Item Passive Ticks (Mantle of Ra, etc.) ───
  processReactivePassives(hero1, hero2, tick, combatLog);
  processReactivePassives(hero2, hero1, tick, combatLog);
  processItemPassiveTicks(hero1, hero2, tick, combatLog);
  processItemPassiveTicks(hero2, hero1, tick, combatLog);

  // ─── Phase 4: Update CC State ───
  updateCCState(hero1);
  updateCCState(hero2);

  // ─── Phase 5: Process Actions ───
  processAction(hero1, hero2, tick, combatLog);
  processAction(hero2, hero1, tick, combatLog);

  // ─── Phase 6: Check Death ───
  checkDeath(hero1, hero2, tick, combatLog, gameState);
  checkDeath(hero2, hero1, tick, combatLog, gameState);

  // ─── Phase 7: Process Hero Passives (periodic) ───
  processHeroPassiveTick(hero1, hero2, tick, combatLog);
  processHeroPassiveTick(hero2, hero1, tick, combatLog);

  // Advance tick
  gameState.tick++;
  gameState.timeSeconds = gameState.tick * TICK_MS / 1000;

  return gameState;
}

// ─── Duration Updates ───

function updateBuffDurations(hero, tick, log) {
  hero.buffs = hero.buffs.filter(buff => {
    if (buff.permanent) return true;
    buff.remainingTicks--;
    if (buff.remainingTicks <= 0) {
      log.addEvent(tick, 'BUFF_EXPIRED', {
        hero: hero.heroName,
        buffName: buff.name || buff.source,
      });
      return false;
    }
    return true;
  });

  hero.debuffs = hero.debuffs.filter(debuff => {
    if (debuff.permanent) return true;
    debuff.remainingTicks--;
    if (debuff.remainingTicks <= 0) {
      log.addEvent(tick, 'DEBUFF_EXPIRED', {
        hero: hero.heroName,
        debuffName: debuff.name || debuff.source,
      });
      return false;
    }
    return true;
  });
}

function updateShieldDurations(hero, tick) {
  hero.shields = hero.shields.filter(shield => {
    if (shield.permanent) return shield.amount > 0;
    shield.remainingTicks--;
    return shield.remainingTicks > 0 && shield.amount > 0;
  });
}

function updateCooldowns(hero) {
  for (const key of Object.keys(hero.cooldowns)) {
    if (hero.cooldowns[key] > 0) {
      hero.cooldowns[key]--;
    }
  }

  // Item passive cooldowns
  for (const key of Object.keys(hero.itemPassiveState)) {
    const state = hero.itemPassiveState[key];
    if (state && state.cooldown > 0) {
      state.cooldown--;
    }
  }

  // Hero passive cooldown
  if (hero.heroPassiveState.internalCD > 0) {
    hero.heroPassiveState.internalCD--;
  }
}

function updateStateFlags(hero) {
  hero.isUntargetable = hero.buffs.some((b) => b.type === BuffType.UNTARGETABLE);
  hero.isInvulnerable = hero.buffs.some((b) => b.type === BuffType.INVULNERABLE);
}

function updateCCState(hero) {
  hero.isStunned = hero.debuffs.some(d =>
    d.type === BuffType.CC && HARD_CC.includes(d.ccType)
  );
  hero.isSilenced = hero.debuffs.some(d =>
    d.type === BuffType.CC && (HARD_CC.includes(d.ccType) || SILENCE_CC.includes(d.ccType))
  );
}

// ─── DoT/HoT Processing ───

function processDoTs(source, target, tick, log) {
  for (const debuff of target.debuffs) {
    if (debuff.type !== BuffType.DOT) continue;
    if (tick % debuff.tickInterval !== 0) continue; // DoT ticks on interval

    const dotDamage = debuff.damagePerTick || 0;
    if (dotDamage > 0) {
      applyRawDamage(target, dotDamage, debuff.damageType || DamageType.MAGIC, log, tick, debuff.source);
    }
  }
}

function processHoTs(hero, tick, log) {
  for (const buff of hero.buffs) {
    if (buff.type !== BuffType.HOT) continue;
    if (tick % (buff.tickInterval || TICKS_PER_SECOND) !== 0) continue;

    const healAmount = buff.healPerTick || 0;
    if (healAmount > 0) {
      applyHealing(hero, healAmount, log, tick, buff.source);
    }
  }
}

// ─── Action Processing ───

function processAction(actor, target, tick, log) {
  // Cannot act if stunned
  if (actor.isStunned || !actor.isAlive) return;

  // Check if current action is still in progress
  if (actor.currentAction) {
    actor.actionProgress++;
    const castTicks = Math.round((actor.currentAction.castTime || 0) * TICKS_PER_SECOND);

    if (actor.actionProgress >= castTicks) {
      // Action completes - execute it
      executeAction(actor, target, actor.currentAction, tick, log);
      actor.currentAction = null;
      actor.actionProgress = 0;
    }
    return;
  }

  // Pick next action from queue
  if (actor.actionQueue.length > 0) {
    const nextAction = actor.actionQueue[0];

    // Validate action
    if (canPerformAction(actor, nextAction)) {
      actor.actionQueue.shift();

      const castTime = nextAction.castTime || 0;
      if (castTime > 0) {
        // Start casting
        actor.currentAction = nextAction;
        actor.actionProgress = 0;
        log.addEvent(tick, 'CAST_START', {
          hero: actor.heroName,
          action: nextAction.name,
          castTime,
        });
      } else {
        // Instant action
        executeAction(actor, target, nextAction, tick, log);
      }
    } else {
      // Can't perform - skip or try auto-attack
      if (nextAction.type === ActionType.SKILL && actor.cooldowns[nextAction.skillSlot] > 0) {
        // Skill on CD - insert AA while waiting
      }
    }
  }
}

function canPerformAction(actor, action) {
  if (!actor.isAlive) return false;
  if (actor.isStunned) return false;

  if (action.type === ActionType.AUTO_ATTACK) {
    if (actor.cooldowns['AA'] > 0) return false;
  }

  if (action.type === ActionType.SKILL) {
    if (actor.isSilenced) return false;
    if (actor.cooldowns[action.skillSlot] > 0) return false;
    if (action.manaCost && actor.currentMana < action.manaCost) return false;
  }

  if (action.type === ActionType.SPELL) {
    const key = `SPELL:${action.spellName || action.name || 'Unknown'}`;
    if (actor.cooldowns[key] > 0) return false;
  }

  return true;
}

function executeAction(actor, target, action, tick, log) {
  if (action.type === ActionType.AUTO_ATTACK) {
    executeAutoAttack(actor, target, tick, log);
  } else if (action.type === ActionType.SKILL) {
    executeSkill(actor, target, action, tick, log);
  } else if (action.type === ActionType.ITEM_ACTIVE) {
    executeItemActive(actor, target, action, tick, log);
  } else if (action.type === ActionType.SPELL) {
    executeSpell(actor, target, action, tick, log);
  }
}

// ============================================================================
// SPELL EXECUTION
// ============================================================================

function executeSpell(actor, target, action, tick, log) {
  const spellDef = CHALLENGER_SPELLS[action.spellName];
  if (!spellDef) return;

  const spellCdKey = `SPELL:${spellDef.name}`;
  if (spellDef.cooldown) {
    actor.cooldowns[spellCdKey] = Math.round(spellDef.cooldown * TICKS_PER_SECOND);
  }

  log.addEvent(tick, 'CAST_START', {
    hero: actor.heroName,
    action: `[Spell] ${spellDef.name}`,
    castTime: 0,
  });

  // Prepare context with helpers
  const ctx = {
    applyBuff,
    applyHealing,
    applyCC,
    calculateDamage,
  };

  // Execute spell logic
  const result = spellDef.execute(actor, target, tick, log, ctx);

  // If result returns a damage bucket, process it
  if (result) {
    const breakdown = calculateDamage(actor, target, result);
    applyDamageResult(target, actor, breakdown, tick, log);
  }
}

// ============================================================================
// AUTO ATTACK EXECUTION
// ============================================================================

function executeAutoAttack(actor, target, tick, log) {
  if (target.isUntargetable) return;

  const stats = getEffectiveStats(actor);
  actor.aaCount++;
  actor.aaCasts++;

  // Base AA damage
  const damageSource = {
    name: 'Auto Attack',
    baseDamage: 0,
    adScaling: 1.0, // AA = 100% AD
    apScaling: 0,
    damageType: DamageType.PHYSICAL,
    canCrit: true,
    isAA: true,
  };

  // Check for Next-AA-Bonus buffs (Omni Arms, Frost Cape, Apocalypse, etc.)
  const nextAABonuses = actor.buffs.filter(b => b.type === BuffType.NEXT_AA_BONUS);
  for (const bonus of nextAABonuses) {
    // Remove the buff (consumed)
    actor.buffs = actor.buffs.filter(b => b !== bonus);

    // Apply bonus damage
    if (bonus.bonusDamage) {
      const bonusBreakdown = calculateDamage(actor, target, {
        name: bonus.name || bonus.source,
        baseDamage: bonus.bonusDamage.base || 0,
        adScaling: bonus.bonusDamage.adScaling || 0,
        apScaling: bonus.bonusDamage.apScaling || 0,
        damageType: bonus.bonusDamage.damageType || DamageType.PHYSICAL,
        canCrit: false,
      });
      applyDamageResult(target, actor, bonusBreakdown, tick, log);
    }

    // Apply bonus effects (slow, etc.)
    if (bonus.appliesCC) {
      applyCC(target, bonus.appliesCC, tick, log, bonus.source);
    }

    log.addEvent(tick, 'ITEM_PROC', {
      hero: actor.heroName,
      item: bonus.source,
      effect: bonus.name,
    });
  }

  // Calculate and apply AA damage
  const breakdown = calculateDamage(actor, target, damageSource);
  applyDamageResult(target, actor, breakdown, tick, log);

  // Process on-hit item passives
  processOnHitPassives(actor, target, tick, log, breakdown);

  if (breakdown.isCrit) {
    processOnCritPassives(actor, target, tick, log);
  }

  // Process hero passive (on AA)
  processHeroPassiveOnHit(actor, target, tick, log, 'AA');

  // Life Steal
  if (stats.life_steal > 0 && breakdown.finalDamage > 0) {
    const heal = Math.round(breakdown.finalDamage * stats.life_steal);
    applyHealing(actor, heal, log, tick, 'Life Steal');
  }

  // Set AA cooldown based on attack speed
  const baseAACD = computeAACooldownTicks(stats.attack_speed);
  actor.cooldowns['AA'] = baseAACD;

  if (breakdown.isCrit) actor.critCount++;

  log.addEvent(tick, 'AUTO_ATTACK', {
    hero: actor.heroName,
    target: target.heroName,
    damage: breakdown.finalDamage,
    isCrit: breakdown.isCrit,
    breakdown: breakdown.stages,
  });
}

function computeAACooldownTicks(attackSpeedPct) {
  // Base AA interval ~1.0s, reduced by attack speed
  const baseInterval = 1.0; // seconds
  const interval = baseInterval / (1 + (attackSpeedPct || 0));
  return Math.max(1, Math.round(interval * TICKS_PER_SECOND));
}

// ============================================================================
// SKILL EXECUTION
// ============================================================================

function executeSkill(actor, target, action, tick, log) {
  if (target.isUntargetable && !action.aoe) return;

  const stats = getEffectiveStats(actor);
  actor.skillsUsed++;
  actor.lastSkillTick = tick;

  // Consume mana
  if (action.manaCost) {
    actor.currentMana = Math.max(0, actor.currentMana - action.manaCost);
  }

  // Set cooldown (with CDR)
  if (action.cooldown) {
    const effectiveCD = action.cooldown * (1 - stats.cooldown_reduction);
    actor.cooldowns[action.skillSlot] = Math.round(effectiveCD * TICKS_PER_SECOND);
  }

  // Execute-style skills: instantly finish low-HP targets.
  if (action.executeThresholdPct && target.maxHP > 0) {
    const hpPct = target.currentHP / target.maxHP;
    if (hpPct <= action.executeThresholdPct) {
      const executeDmg = target.currentHP;
      applyRawDamage(target, executeDmg, DamageType.TRUE, log, tick, action.name || 'Execute');
      actor.totalDamageDealt += executeDmg;
      log.addEvent(tick, 'SKILL_USE', {
        hero: actor.heroName,
        target: target.heroName,
        skill: action.name,
        slot: action.skillSlot,
        totalDamage: executeDmg,
        instances: 1,
        execute: true,
      });
      return;
    }
  }

  // Calculate damage for each hit instance
  const instances = action.damageInstances || 1;
  let totalSkillDamage = 0;

  for (let i = 0; i < instances; i++) {
    const perHitBase = (action.baseDamage || 0) / instances;
    const damageSource = {
      name: action.name,
      baseDamage: perHitBase,
      adScaling: (action.adScaling || 0) / instances,
      apScaling: (action.apScaling || 0) / instances,
      damageType: action.damageType || DamageType.PHYSICAL,
      canCrit: action.canCrit || false,
      hpMaxScaling: action.hpMaxScaling ? action.hpMaxScaling / instances : 0,
      hpCurrentScaling: action.hpCurrentScaling ? action.hpCurrentScaling / instances : 0,
      targetMaxScaling: action.targetMaxScaling ? action.targetMaxScaling / instances : 0,
      targetCurrentHP: target.currentHP,
      targetMaxHP: target.maxHP,
      targetLostHP: target.maxHP - target.currentHP,
    };

    const breakdown = calculateDamage(actor, target, damageSource);
    applyDamageResult(target, actor, breakdown, tick, log);
    totalSkillDamage += breakdown.finalDamage;

    if (breakdown.isCrit) actor.critCount++;
  }

  // Apply CC
  if (action.ccType && action.ccDuration) {
    applyCC(target, {
      ccType: action.ccType,
      duration: action.ccDuration,
    }, tick, log, action.name);
  }

  // Apply skill-specific buffs
  if (action.grantsBuff) {
    applyBuff(actor, action.grantsBuff, tick, log);
  }

  // Untargetable/Invulnerable
  if (action.isUntargetable) {
    actor.isUntargetable = true;
    actor.buffs.push({
      type: BuffType.UNTARGETABLE,
      name: `${action.name} Untargetable`,
      remainingTicks: Math.round((action.untargetableDuration || 1) * TICKS_PER_SECOND),
      source: action.name,
    });
  }

  // Trigger after-skill item passives (Omni Arms, Frost Cape, Apocalypse)
  processAfterSkillPassives(actor, target, tick, log);
  processOnSkillHitPassives(actor, target, tick, log);

  // Process hero passive on skill use
  processHeroPassiveOnHit(actor, target, tick, log, 'SKILL');

  // Spell Vamp
  if (stats.magic_life_steal > 0 && totalSkillDamage > 0) {
    const vamp = Math.round(totalSkillDamage * stats.magic_life_steal);
    applyHealing(actor, vamp, log, tick, 'Spell Vamp');
  }

  log.addEvent(tick, 'SKILL_USE', {
    hero: actor.heroName,
    target: target.heroName,
    skill: action.name,
    slot: action.skillSlot,
    totalDamage: totalSkillDamage,
    instances,
    ccType: action.ccType,
    ccDuration: action.ccDuration,
  });
}

// ============================================================================
// ITEM PASSIVES SYSTEM
// ============================================================================

// Process on-hit item passives (triggered by AA)
function processOnHitPassives(actor, target, tick, log, aaBreakdown) {
  for (const item of actor.items) {
    if (!item || !item.passive) continue;
    const passive = item.passive;
    const stateKey = item.name;

    if (!actor.itemPassiveState[stateKey]) {
      actor.itemPassiveState[stateKey] = { cooldown: 0, stacks: 0, hitCount: 0 };
    }
    const state = actor.itemPassiveState[stateKey];

    if (passive.trigger !== 'ON_HIT' && !(passive.trigger === 'ALWAYS' && passive.effect)) continue;
    if (state.cooldown > 0) continue;

    if (passive.requiredHits && passive.requiredHits > 1) {
      state.hitCount = (state.hitCount || 0) + 1;
      if (state.hitCount % passive.requiredHits !== 0) {
        continue;
      }
    }

    // Execute passive effect
    executeItemPassive(actor, target, item, passive, state, tick, log, 'AA');
  }
}

// Process after-skill item passives
function processAfterSkillPassives(actor, target, tick, log) {
  for (const item of actor.items) {
    if (!item || !item.passive) continue;
    const passive = item.passive;
    if (passive.trigger !== 'AFTER_SKILL') continue;

    const stateKey = item.name;
    if (!actor.itemPassiveState[stateKey]) {
      actor.itemPassiveState[stateKey] = { cooldown: 0, stacks: 0 };
    }
    const state = actor.itemPassiveState[stateKey];
    if (state.cooldown > 0) continue;

    // Some parsed/defined passives deal immediate damage or apply effects on skill use.
    if (passive.effect) {
      executeItemPassive(actor, target, item, passive, state, tick, log, 'SKILL');
    }

    // Grant Next-AA-Bonus buff
    if (passive.nextAABonus) {
      const bonusDuration = Math.round((passive.stackDuration || 5) * TICKS_PER_SECOND);
      actor.buffs.push({
        type: BuffType.NEXT_AA_BONUS,
        name: passive.name,
        source: item.name,
        bonusDamage: passive.nextAABonus.damage,
        appliesCC: passive.nextAABonus.cc,
        remainingTicks: bonusDuration,
        permanent: false,
      });
    }

    // Set cooldown
    if (passive.cooldown) {
      state.cooldown = Math.round(passive.cooldown * TICKS_PER_SECOND);
    }

    log.addEvent(tick, 'ITEM_PASSIVE_READY', {
      hero: actor.heroName,
      item: item.name,
      passive: passive.name,
    });
  }
}

function processOnSkillHitPassives(actor, target, tick, log) {
  for (const item of actor.items) {
    if (!item || !item.passive) continue;
    const passive = item.passive;
    if (passive.trigger !== 'ON_SKILL_HIT') continue;

    const stateKey = item.name;
    if (!actor.itemPassiveState[stateKey]) {
      actor.itemPassiveState[stateKey] = { cooldown: 0, stacks: 0, hitCount: 0 };
    }
    const state = actor.itemPassiveState[stateKey];
    if (state.cooldown > 0) continue;

    executeItemPassive(actor, target, item, passive, state, tick, log, 'SKILL_HIT');
  }
}

function processOnDamageTakenPassives(defender, attacker, tick, log) {
  for (const item of defender.items) {
    if (!item || !item.passive) continue;
    const passive = item.passive;
    if (passive.trigger !== 'ON_DAMAGE_TAKEN') continue;

    const stateKey = item.name;
    if (!defender.itemPassiveState[stateKey]) {
      defender.itemPassiveState[stateKey] = { cooldown: 0, stacks: 0, hitCount: 0 };
    }
    const state = defender.itemPassiveState[stateKey];
    if (state.cooldown > 0) continue;

    executeItemPassive(defender, attacker, item, passive, state, tick, log, 'DAMAGE_TAKEN');
  }
}

function processOnCritPassives(actor, target, tick, log) {
  for (const item of actor.items) {
    if (!item || !item.passive) continue;
    const passive = item.passive;
    if (passive.trigger !== 'ON_CRIT') continue;

    const stateKey = item.name;
    if (!actor.itemPassiveState[stateKey]) {
      actor.itemPassiveState[stateKey] = { cooldown: 0, stacks: 0, hitCount: 0 };
    }
    const state = actor.itemPassiveState[stateKey];
    if (state.cooldown > 0) continue;

    executeItemPassive(actor, target, item, passive, state, tick, log, 'CRIT');
  }
}

function processReactivePassives(actor, target, tick, log) {
  for (const item of actor.items) {
    if (!item || !item.passive) continue;
    const passive = item.passive;
    if (passive.trigger !== 'CONDITIONAL' && passive.trigger !== 'AURA') continue;

    if (passive.trigger === 'AURA' && tick % 3 !== 0) continue;

    const stateKey = item.name;
    if (!actor.itemPassiveState[stateKey]) {
      actor.itemPassiveState[stateKey] = { cooldown: 0, stacks: 0, hitCount: 0 };
    }
    const state = actor.itemPassiveState[stateKey];
    if (state.cooldown > 0) continue;

    executeItemPassive(actor, target, item, passive, state, tick, log, 'TICK');
  }
}

// Process periodic item passives (Mantle of Ra burn, etc.)
function processItemPassiveTicks(actor, target, tick, log) {
  for (const item of actor.items) {
    if (!item || !item.passive) continue;
    const passive = item.passive;
    if (passive.trigger !== 'PERIODIC') continue;

    // Check tick interval
    if (tick % (passive.tickInterval || TICKS_PER_SECOND) !== 0) continue;

    const stateKey = item.name;
    if (!actor.itemPassiveState[stateKey]) {
      actor.itemPassiveState[stateKey] = { cooldown: 0 };
    }

    executeItemPassive(actor, target, item, passive, actor.itemPassiveState[stateKey], tick, log, 'PERIODIC');
  }
}

function executeItemPassive(actor, target, item, passive, state, tick, log, triggerType = null) {
  const stats = getEffectiveStats(actor);
  let procDetail = passive.name || passive.effect || 'Passive';
  let didProc = false;

  if (typeof passive.apply === 'function') {
    const applyResult = passive.apply(actor, target, tick, log, state, triggerType);
    didProc = passive.trigger !== 'ALWAYS';

    if (applyResult && typeof applyResult.bonusDamage === 'number' && applyResult.bonusDamage > 0) {
      const breakdown = calculateDamage(actor, target, {
        name: `${item.name} (${passive.name || 'Passive'})`,
        baseDamage: applyResult.bonusDamage,
        damageType: applyResult.damageType || DamageType.PHYSICAL,
        canCrit: false,
      });
      applyDamageResult(target, actor, breakdown, tick, log);
      procDetail = `${passive.name || 'Passive'} (+${Math.round(breakdown.finalDamage)} DMG)`;
      didProc = true;
    }
  }

  // Handle different passive effects
  if (passive.effect === 'BONUS_PHYSICAL_DAMAGE') {
    let bonusDmg = passive.flatDamage || 0;
    if (passive.targetHPPct) {
      bonusDmg += target.currentHP * passive.targetHPPct;
    }
    if (passive.adScaling) {
      bonusDmg += stats.phys_atk * passive.adScaling;
    }

    const breakdown = calculateDamage(actor, target, {
      name: `${item.name} (${passive.name})`,
      baseDamage: bonusDmg,
      damageType: DamageType.PHYSICAL,
      canCrit: false,
    });
    applyDamageResult(target, actor, breakdown, tick, log);
    procDetail = `${passive.name || 'Passive'} (+${Math.round(breakdown.finalDamage)} Physical)`;
    didProc = true;
  }

  if (passive.effect === 'BONUS_MAGIC_DAMAGE') {
    let bonusDmg = passive.flatDamage || 0;
    if (passive.apScaling) {
      bonusDmg += stats.magic_power * passive.apScaling;
    }
    if (passive.adScaling) {
      bonusDmg += stats.phys_atk * passive.adScaling;
    }
    if (passive.targetMaxHPPct) {
      bonusDmg += target.maxHP * passive.targetMaxHPPct;
    }

    const breakdown = calculateDamage(actor, target, {
      name: `${item.name} (${passive.name})`,
      baseDamage: bonusDmg,
      damageType: DamageType.MAGIC,
      canCrit: false,
    });
    applyDamageResult(target, actor, breakdown, tick, log);
    procDetail = `${passive.name || 'Passive'} (+${Math.round(breakdown.finalDamage)} Magic)`;
    didProc = true;
  }

  if (passive.effect === 'APPLY_DOT') {
    target.debuffs.push({
      type: BuffType.DOT,
      name: passive.name,
      source: item.name,
      damagePerTick: passive.dotDamage || 0,
      damageType: passive.dotDamageType || DamageType.MAGIC,
      tickInterval: passive.dotInterval || TICKS_PER_SECOND,
      remainingTicks: Math.round((passive.dotDuration || 3) * TICKS_PER_SECOND),
    });
    didProc = true;
  }

  if (passive.effect === 'SLOW') {
    applyCC(target, {
      ccType: CCType.SLOW,
      duration: passive.slowDuration || 1,
      value: passive.slowPct || 0.3,
    }, tick, log, item.name);
    didProc = true;
  }

  if (passive.effect === 'ATTACK_SPEED_STACK') {
    state.stacks = Math.min((state.stacks || 0) + 1, passive.maxStacks || 5);
    // Add/refresh buff
    actor.buffs = actor.buffs.filter(b => b.source !== item.name);
    actor.buffs.push({
      type: BuffType.STAT_PCT,
      stat: 'attack_speed',
      value: (passive.asPerStack || 0.1) * state.stacks,
      name: `${passive.name} x${state.stacks}`,
      source: item.name,
      remainingTicks: Math.round((passive.stackDuration || 2) * TICKS_PER_SECOND),
    });
    procDetail = `${passive.name || 'Passive'} x${state.stacks}`;
    didProc = true;
  }

  if (passive.effect === 'ARMOR_SHRED') {
    state.stacks = Math.min((state.stacks || 0) + 1, passive.maxStacks || 4);
    target.debuffs = target.debuffs.filter(d => d.source !== item.name);
    target.debuffs.push({
      type: BuffType.STAT_PCT,
      stat: 'phys_defense',
      value: (passive.shredPct || 0.06) * state.stacks,
      name: `${passive.name} x${state.stacks}`,
      source: item.name,
      remainingTicks: Math.round((passive.shredDuration || 5) * TICKS_PER_SECOND),
    });
    procDetail = `${passive.name || 'Passive'} x${state.stacks}`;
    didProc = true;
  }

  if (passive.effect === 'REFLECT_DAMAGE') {
    // Handled in damage pipeline when defender takes physical damage
    // This flag is checked in applyDamageResult
  }

  // Set cooldown
  if (passive.cooldown) {
    state.cooldown = Math.round(passive.cooldown * TICKS_PER_SECOND);
  }

  if (didProc) {
    log.addEvent(tick, 'ITEM_PROC', {
      hero: actor.heroName,
      item: item.name,
      effect: procDetail,
    });
  }
}

// ============================================================================
// HERO PASSIVES SYSTEM (Stub - populated by HeroPassives.js)
// ============================================================================

// Will be overridden by HeroPassives.js registration
let heroPassiveHandlers = {};

export function registerHeroPassive(heroName, handler) {
  heroPassiveHandlers[heroName] = handler;
}

function processHeroPassiveOnHit(actor, target, tick, log, triggerType) {
  const handler = heroPassiveHandlers[actor.heroName];
  if (handler && handler.onHit) {
    handler.onHit(actor, target, tick, log, triggerType);
  }
}

function processHeroPassiveTick(actor, target, tick, log) {
  const handler = heroPassiveHandlers[actor.heroName];
  if (handler && handler.onTick) {
    handler.onTick(actor, target, tick, log);
  }
}

export function processHeroPassiveOnDamageTaken(defender, attacker, damage, tick, log) {
  const handler = heroPassiveHandlers[defender.heroName];
  if (handler && handler.onDamageTaken) {
    handler.onDamageTaken(defender, attacker, damage, tick, log);
  }
}

// ============================================================================
// DAMAGE APPLICATION
// ============================================================================

function applyDamageResult(target, attacker, breakdown, tick, log) {
  const damage = breakdown.finalDamage;
  if (damage <= 0) return;

  target.currentHP = Math.max(0, target.currentHP - damage);
  target.totalDamageTaken += damage;
  attacker.totalDamageDealt += damage;

  // Check Mail of Pain reflection
  if (breakdown.damageType === DamageType.PHYSICAL) {
    const hasMailOfPain = target.items.some(i => i && i.name === 'Mail of Pain');
    if (hasMailOfPain) {
      const reflectDmg = Math.round(damage * 0.15);
      if (reflectDmg > 0) {
        applyRawDamage(attacker, reflectDmg, DamageType.MAGIC, log, tick, 'Mail of Pain');
        log.addEvent(tick, 'DAMAGE_REFLECT', {
          hero: target.heroName,
          target: attacker.heroName,
          damage: reflectDmg,
          source: 'Mail of Pain',
        });
      }
    }
  }

  // Trigger hero passive on damage taken
  processHeroPassiveOnDamageTaken(target, attacker, damage, tick, log);
  processOnDamageTakenPassives(target, attacker, tick, log);

  log.addEvent(tick, 'DAMAGE_DEALT', {
    source: attacker.heroName,
    target: target.heroName,
    damage,
    damageType: breakdown.damageType,
    isCrit: breakdown.isCrit,
    sourceName: breakdown.source,
  });
}

function applyRawDamage(target, amount, damageType, log, tick, source) {
  // Raw damage (no pipeline - used for reflection, DoT, etc.)
  const finalDmg = Math.max(0, Math.round(amount));
  target.currentHP = Math.max(0, target.currentHP - finalDmg);
  target.totalDamageTaken += finalDmg;
}

export function applyHealing(hero, amount, log, tick, source) {
  let scaledAmount = amount;
  const healingReduce = hero.debuffs
    .filter((d) => d.stat === 'healing_received' && d.type === BuffType.STAT_PCT)
    .reduce((acc, d) => acc + (d.value || 0), 0);
  if (healingReduce > 0) {
    scaledAmount = amount * Math.max(0, 1 - Math.min(0.9, healingReduce));
  }

  const actualHeal = Math.min(scaledAmount, hero.maxHP - hero.currentHP);
  hero.currentHP += actualHeal;
  hero.totalHealing += actualHeal;

  if (actualHeal > 0) {
    log.addEvent(tick, 'HEALING', {
      hero: hero.heroName,
      amount: actualHeal,
      source,
    });
  }
}

// ============================================================================
// CC APPLICATION
// ============================================================================

function applyCC(target, cc, tick, log, source) {
  let duration = cc.duration || 1;

  // Gilded Greaves: -35% CC duration
  const hasGilded = target.items.some(i => i && i.name === 'Gilded Greaves');
  if (hasGilded) {
    duration *= 0.65; // Reduce by 35%
  }

  // Check CC immunity
  if (target.isInvulnerable || target.isUntargetable) return;

  const durationTicks = Math.round(duration * TICKS_PER_SECOND);

  target.debuffs.push({
    type: BuffType.CC,
    ccType: cc.ccType,
    name: `${source} - ${cc.ccType}`,
    source,
    value: cc.value || 0, // for slow amount
    remainingTicks: durationTicks,
  });

  // Update CC state immediately
  updateCCState(target);

  // Track stats
  if (HARD_CC.includes(cc.ccType)) {
    // Count the applier's cc stats
  }

  log.addEvent(tick, 'CC_APPLIED', {
    target: target.heroName,
    source,
    ccType: cc.ccType,
    duration: duration.toFixed(2),
  });
}

export function applyBuff(hero, buffDef, tick, log) {
  hero.buffs.push({
    ...buffDef,
    remainingTicks: Math.round((buffDef.duration || 3) * TICKS_PER_SECOND),
  });

  log.addEvent(tick, 'BUFF_APPLIED', {
    hero: hero.heroName,
    buff: buffDef.name,
    source: buffDef.source,
    duration: buffDef.duration,
  });
}

// ============================================================================
// DEATH CHECK
// ============================================================================

function checkDeath(hero, opponent, tick, log, gameState) {
  if (hero.currentHP <= 0 && hero.isAlive) {
    // Check Blade of Eternity
    const boeState = hero.itemPassiveState['Blade of Eternity'];
    const hasBOE = hero.items.some(i => i && i.name === 'Blade of Eternity');

    if (hasBOE && (!boeState || !boeState.used)) {
      // Resurrect
      const resurrectHP = 2000 + hero.level * 100;
      hero.currentHP = resurrectHP;
      hero.isAlive = true;

      if (!hero.itemPassiveState['Blade of Eternity']) {
        hero.itemPassiveState['Blade of Eternity'] = {};
      }
      hero.itemPassiveState['Blade of Eternity'].used = true;

      log.addEvent(tick, 'RESURRECT', {
        hero: hero.heroName,
        hp: resurrectHP,
        source: 'Blade of Eternity',
      });
      return;
    }

    hero.isAlive = false;
    hero.isDead = true;
    gameState.isFinished = true;
    gameState.winner = opponent.heroName;

    log.addEvent(tick, 'DEATH', {
      hero: hero.heroName,
      killer: opponent.heroName,
      time: (tick * TICK_MS / 1000).toFixed(1),
    });
  }
}

// ============================================================================
// ITEM ACTIVE EXECUTION
// ============================================================================

function executeItemActive(actor, target, action, tick, log) {
  const item = action.item;
  if (!item || !item.active) return;

  const stateKey = `${item.name}_active`;
  if (!actor.itemPassiveState[stateKey]) {
    actor.itemPassiveState[stateKey] = { cooldown: 0 };
  }

  const state = actor.itemPassiveState[stateKey];
  if (state.cooldown > 0) return;

  const active = item.active;

  // Arctic Orb: Solidify
  if (active.effect === 'INVULNERABLE') {
    actor.isInvulnerable = true;
    actor.buffs.push({
      type: BuffType.INVULNERABLE,
      name: active.name,
      source: item.name,
      remainingTicks: Math.round((active.duration || 2) * TICKS_PER_SECOND),
    });
  }

  // Rock Shield: Pain Absorb
  if (active.effect === 'SHIELD') {
    const shieldAmount = (actor.maxHP * (active.hpPct || 0.3)) + ((actor.maxHP - actor.currentHP) * (active.lostHpPct || 0.1));
    actor.shields.push({
      amount: shieldAmount,
      remainingTicks: Math.round((active.duration || 3) * TICKS_PER_SECOND),
      source: item.name,
    });
  }

  // Bow of Slaughter: Bloodthirst
  if (active.effect === 'LIFESTEAL_BOOST') {
    actor.buffs.push({
      type: BuffType.STAT_FLAT,
      stat: 'life_steal',
      value: active.lifeStealBonus || 0.6,
      name: active.name,
      source: item.name,
      remainingTicks: Math.round((active.duration || 3) * TICKS_PER_SECOND),
    });
  }

  state.cooldown = Math.round((active.cooldown || 60) * TICKS_PER_SECOND);

  log.addEvent(tick, 'ITEM_ACTIVE', {
    hero: actor.heroName,
    item: item.name,
    effect: active.name,
  });
}

// ============================================================================
// SIMULATION RUNNER
// ============================================================================

/**
 * Run simulation for specified duration or until one hero dies
 * @param {object} gameState - created by createGameState
 * @param {number} maxDuration - max seconds to simulate
 * @returns {object} final game state
 */
export function runSimulation(gameState, maxDuration = 30) {
  const maxTicks = maxDuration * TICKS_PER_SECOND;

  while (gameState.tick < maxTicks && !gameState.isFinished) {
    processTick(gameState);
  }

  // If nobody died, determine winner by remaining HP%
  if (!gameState.isFinished) {
    const hp1Pct = gameState.hero1.currentHP / gameState.hero1.maxHP;
    const hp2Pct = gameState.hero2.currentHP / gameState.hero2.maxHP;
    gameState.winner = hp1Pct >= hp2Pct ? gameState.hero1.heroName : gameState.hero2.heroName;
    gameState.isFinished = true;
  }

  return gameState;
}

/**
 * Run simulation tick by tick (for UI playback)
 * Returns generator that yields state after each tick
 */
export function* tickByTickSimulation(gameState, maxDuration = 30) {
  const maxTicks = maxDuration * TICKS_PER_SECOND;

  while (gameState.tick < maxTicks && !gameState.isFinished) {
    processTick(gameState);
    yield { ...gameState };
  }
}

// ============================================================================
// COMBO BUILDER HELPERS
// ============================================================================

/**
 * Build action queue from skill data
 * @param {object} heroState
 * @param {array} skills - hero's skills from Excel data  
 * @param {array} combo - ['AA', 'S1', 'AA', 'S2', 'AA', 'ULT']
 */
export function buildActionQueue(heroState, skills, combo) {
  const queue = [];

  for (const action of combo) {
    if (action === 'AA') {
      queue.push({
        type: ActionType.AUTO_ATTACK,
        name: 'Auto Attack',
        castTime: 0, // instant
      });
    } else {
      // Find skill data
      const skillMap = {
        'S1': 'Skill 1',
        'S2': 'Skill 2',
        'ULT': 'Ultimate',
        'PASSIVE': 'Passive',
      };
      const slotName = skillMap[action] || action;
      const skillData = skills.find(s => (s.skill_slot || s.slot) === slotName);

      if (skillData) {
        const inferred = inferSkillEffectsFromDescription(skillData || {});
        const rank = heroState.skillRanks[slotName] || 1;
        const rankIdx = rank - 1;

        queue.push({
          type: ActionType.SKILL,
          name: skillData.skill_name || skillData.name,
          skillSlot: slotName,
          baseDamage: getSkillValueAtRank(skillData.base_damage || skillData.base_dmg, rankIdx),
          adScaling: skillData.ad_scaling || 0,
          apScaling: skillData.ap_scaling || 0,
          damageType: skillData.damage_type || inferred.damageType || DamageType.PHYSICAL,
          cooldown: getSkillValueAtRank(skillData.cooldown, rankIdx),
          manaCost: getSkillValueAtRank(skillData.mana_cost, rankIdx),
          castTime: skillData.cast_time || 0.3,
          damageInstances: skillData.damage_instances || inferred.damageInstances || 1,
          canCrit: skillData.can_crit || false,
          ccType: skillData.cc_type || inferred.ccType || null,
          ccDuration: skillData.cc_duration || inferred.ccDuration || 0,
          grantsBuff: skillData.grants_buff || inferred.grantsBuff || null,
          isUntargetable: skillData.is_untargetable || inferred.isUntargetable || false,
          untargetableDuration: skillData.untargetable_duration || inferred.untargetableDuration || 0,
          hpMaxScaling: skillData.hp_max_scaling || inferred.hpMaxScaling || 0,
          hpCurrentScaling: skillData.hp_current_scaling || inferred.targetCurrentHpScaling || 0,
          targetMaxScaling: inferred.targetMaxHpScaling || 0,
          hpLostScaling: skillData.hp_lost_scaling || inferred.targetLostHpScaling || 0,
          executeThresholdPct: inferred.executeThresholdPct || 0,
          aoe: skillData.aoe || false,
        });
      }
    }
  }

  return queue;
}

function getSkillValueAtRank(valueArray, rankIdx) {
  if (!valueArray) return 0;
  if (Array.isArray(valueArray)) {
    return valueArray[Math.min(rankIdx, valueArray.length - 1)] || 0;
  }
  return valueArray;
}

// ============================================================================
// SIMULATION STATISTICS
// ============================================================================

export function getSimulationStats(gameState) {
  const h1 = gameState.hero1;
  const h2 = gameState.hero2;

  return {
    duration: gameState.timeSeconds,
    winner: gameState.winner,
    hero1: {
      name: h1.heroName,
      hpRemaining: h1.currentHP,
      hpPct: Math.round((h1.currentHP / h1.maxHP) * 100),
      totalDamageDealt: h1.totalDamageDealt,
      totalDamageTaken: h1.totalDamageTaken,
      totalHealing: h1.totalHealing,
      dps: h1.totalDamageDealt / Math.max(1, gameState.timeSeconds),
      skillsUsed: h1.skillsUsed,
      aaCasts: h1.aaCasts,
      critCount: h1.critCount,
      critRate: h1.aaCasts > 0 ? (h1.critCount / h1.aaCasts * 100).toFixed(1) : '0.0',
    },
    hero2: {
      name: h2.heroName,
      hpRemaining: h2.currentHP,
      hpPct: Math.round((h2.currentHP / h2.maxHP) * 100),
      totalDamageDealt: h2.totalDamageDealt,
      totalDamageTaken: h2.totalDamageTaken,
      totalHealing: h2.totalHealing,
      dps: h2.totalDamageDealt / Math.max(1, gameState.timeSeconds),
      skillsUsed: h2.skillsUsed,
      aaCasts: h2.aaCasts,
      critCount: h2.critCount,
      critRate: h2.aaCasts > 0 ? (h2.critCount / h2.aaCasts * 100).toFixed(1) : '0.0',
    },
    events: gameState.combatLog.getEvents(),
    eventCount: gameState.combatLog.getEvents().length,
  };
}
